import { Component, inject, input, output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EditMode } from '@imagise/entities';
import { isEditableTarget } from '@imagise/shared';
import { MessagingService } from '../../infrastructure/messaging/messaging.service';
import { Size2D } from '../../domain/interfaces/shared';
import { EditorOptions } from '../../domain/interfaces/model-options';
import { RefreshEditOptionsMessage } from '../../domain/models/messages';
import { ImageCommandsService } from '../../../command/image-commands.service';
import { CropPanelComponent } from './crop-panel/crop-panel.component';
import { ResizePanelComponent } from './resize-panel/resize-panel.component';
import { RotatePanelComponent } from './rotate-panel/rotate-panel.component';
import { FilterPanelComponent } from './filter-panel/filter-panel.component';
import { WatermarkPanelComponent } from './watermark-panel/watermark-panel.component';
import { DeskewPanelComponent } from './deskew-panel/deskew-panel.component';
import { EditorDraftOperation, EditorFormStore } from '../../domain';
import { AnimationPanelComponent } from './animation-panel/animation-panel.component';
import { MergePanelComponent } from './merge-panel/merge-panel.component';
import { ObjectDetectionPanelComponent } from './object-detection-panel/object-detection-panel.component';
import type { ObjectDetectionBox } from '../../infrastructure/rendering';

@Component({
  selector: 'lib-sub-menu',
  templateUrl: './sub-menu.component.html',
  styleUrls: ['./sub-menu.component.scss'],
  imports: [
    CropPanelComponent,
    ResizePanelComponent,
    RotatePanelComponent,
    FilterPanelComponent,
    WatermarkPanelComponent,
    DeskewPanelComponent,
    AnimationPanelComponent,
    MergePanelComponent,
    ObjectDetectionPanelComponent,
  ],
  host: { '(document:keydown.escape)': 'handleEscape($event)' },
})
export class SubMenuComponent {
  private readonly messageBus = inject(MessagingService);
  private readonly commandSvc = inject(ImageCommandsService);
  private readonly editorStore = inject(EditorFormStore);
  private readonly dialog = inject(MatDialog);


  readonly editMode = input.required<EditMode>();

  readonly imageSize = input<Size2D | null>(null);

  /**
   * Editor options
   */
  readonly editorOptions = input<EditorOptions | null>(null);

  editModes = EditMode;

  readonly closePanel = output();

  /**
   * Merge cannot be committed into the /edit stack — the host applies it via
   * the /merge/* endpoints and replaces the source image instead.
   */
  readonly mergeApplied = output<void>();

  readonly detectionReady = input(false);
  readonly detectionLoading = input(false);
  readonly detectedObjects = input<readonly ObjectDetectionBox[] | null>(null);
  readonly detectionErrorKey = input<string | null>(null);
  readonly detectionDownloadUrl = input<string | null>(null);
  readonly detectionRequested = output<void>();
  readonly detectionCancelled = output<void>();

  onDraftChanged(draft: EditorDraftOperation | null): void {
    this.editorStore.setDraftCandidate(draft);
  }

  onMergeApply(): void {
    this.mergeApplied.emit();
  }

  onApply(): void {
    const preparedCommit = this.editorStore.prepareDraftCommit();

    if (preparedCommit) {
      this.commandSvc.push({
        execute: () => {
          this.editorStore.executePreparedCommit(preparedCommit);
          this.messageBus.publish(new RefreshEditOptionsMessage());
        },
        rollback: () => {
          this.editorStore.rollbackPreparedCommit(preparedCommit.id);
          this.messageBus.publish(new RefreshEditOptionsMessage());
        },
      });
    } else {
      this.editorStore.clearDraft();
      this.messageBus.publish(new RefreshEditOptionsMessage());
    }

    this.closePanel.emit();
  }

  onCancel(): void {
    this.editorStore.clearDraft();
    this.messageBus.publish(new RefreshEditOptionsMessage());
    this.closePanel.emit();
  }

  /**
   * Esc discards the draft and closes the panel — same as the Cancel button.
   * Yields to open dialogs (Esc closes them) and editable fields.
   */
  protected handleEscape(event: Event): void {
    if (this.dialog.openDialogs.length > 0 || isEditableTarget(event.target)) {
      return;
    }

    event.preventDefault();
    this.onCancel();
  }
}
